<template>
  <div
    class="bg-background-neutral-secondary rounded-3xl p-4 shadow-[0px_5px_20px_rgba(0,0,0,0.05)]"
  >
    <p
      class="text-text-neutral-primary mb-4 text-base leading-[22px] font-semibold"
    >
      {{
        isPromoApplicable
          ? t('cashbackInfo.countdown.titleActive')
          : t('cashbackInfo.countdown.titleInactive')
      }}
    </p>

    <div class="mb-4 flex w-full items-center gap-1">
      <div
        v-for="unit in countdownUnits"
        :key="unit.key"
        class="flex min-w-0 flex-1 flex-col items-center gap-0.5"
      >
        <div class="relative h-[74px] w-full max-w-[74px]">
          <div
            class="pointer-events-none absolute inset-x-0.5 top-0 h-[66px] rounded-2xl shadow-[1px_1px_3px_0px_rgba(0,0,0,0.2)]"
          >
            <div class="bg-situational-light absolute inset-0 rounded-2xl" />
            <div
              class="absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_2px_0px_rgba(255,255,255,0.2)]"
            />
          </div>
          <div
            class="pointer-events-none absolute inset-x-px top-0.5 h-[68px] rounded-2xl shadow-[1px_1px_3px_0px_rgba(0,0,0,0.2)]"
          >
            <div class="bg-situational-light absolute inset-0 rounded-2xl" />
            <div
              class="absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_2px_0px_rgba(255,255,255,0.2)]"
            />
          </div>
          <div
            class="bg-situational-light absolute inset-x-0 top-1 h-[70px] rounded-2xl"
          />

          <div
            class="absolute inset-x-0 top-1 h-[70px]"
            style="perspective: 300px"
          >
            <div
              class="bg-background-neutral-onblock relative h-[35px] overflow-hidden rounded-tl-2xl rounded-tr-2xl shadow-[1px_1px_1.5px_rgba(0,0,0,0.15)]"
            >
              <div
                class="absolute inset-x-0 top-0 flex h-[70px] items-center justify-center"
              >
                <span
                  :class="[
                    'leading-none font-black whitespace-nowrap',
                    unit.state.currentFontClass,
                    isPromoApplicable
                      ? 'text-text-neutral-primary'
                      : 'text-text-neutral-quaternary',
                  ]"
                >
                  {{ unit.state.current }}
                </span>
              </div>
              <div
                class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_2px_0px_rgba(255,255,255,0.2)]"
              />
            </div>

            <div class="bg-situational-light h-px w-full" />
            <div
              class="bg-background-neutral-onblock relative h-[34px] overflow-hidden rounded-br-2xl rounded-bl-2xl shadow-[1px_1px_1.5px_rgba(0,0,0,0.15)]"
            >
              <div
                class="absolute inset-x-0 bottom-0 flex h-[70px] items-center justify-center"
              >
                <span
                  :class="[
                    'leading-none font-black whitespace-nowrap',
                    unit.state.flipping
                      ? unit.state.previousFontClass
                      : unit.state.currentFontClass,
                    isPromoApplicable
                      ? 'text-text-neutral-primary'
                      : 'text-text-neutral-quaternary',
                  ]"
                >
                  {{
                    unit.state.flipping
                      ? unit.state.previous
                      : unit.state.current
                  }}
                </span>
              </div>
              <div
                class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_1px_1px_2px_0px_rgba(255,255,255,0.2)]"
              />
            </div>

            <div
              v-if="unit.state.flipping"
              class="flip-card-top bg-background-neutral-onblock absolute inset-x-0 top-0 z-10 h-[35px] overflow-hidden rounded-tl-2xl rounded-tr-2xl shadow-[1px_1px_2px_rgba(0,0,0,0.2)]"
            >
              <div
                class="absolute inset-x-0 top-0 flex h-[70px] items-center justify-center"
              >
                <span
                  :class="[
                    'leading-none font-black whitespace-nowrap',
                    unit.state.previousFontClass,
                    isPromoApplicable
                      ? 'text-text-neutral-primary'
                      : 'text-text-neutral-quaternary',
                  ]"
                >
                  {{ unit.state.previous }}
                </span>
              </div>
            </div>

            <div
              v-if="unit.state.flipping"
              class="flip-card-bottom bg-background-neutral-onblock absolute inset-x-0 bottom-0 z-10 h-[34px] overflow-hidden rounded-br-2xl rounded-bl-2xl shadow-[1px_1px_2px_rgba(0,0,0,0.2)]"
            >
              <div
                class="absolute inset-x-0 bottom-0 flex h-[70px] items-center justify-center"
              >
                <span
                  :class="[
                    'leading-none font-black whitespace-nowrap',
                    unit.state.currentFontClass,
                    isPromoApplicable
                      ? 'text-text-neutral-primary'
                      : 'text-text-neutral-quaternary',
                  ]"
                >
                  {{ unit.state.current }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="pointer-events-none absolute inset-x-0 top-1 h-[35px] rounded-tl-2xl rounded-tr-2xl bg-gradient-to-b from-transparent to-black opacity-5"
          />
          <div
            class="pointer-events-none absolute inset-x-0 bottom-0 h-[35px] -scale-y-100 rounded-tl-2xl rounded-tr-2xl bg-gradient-to-b from-transparent to-black opacity-5"
          />
        </div>

        <span
          :class="[
            'text-xs',
            isPromoApplicable
              ? 'text-text-neutral-tertiary'
              : 'text-text-neutral-quaternary',
          ]"
        >
          {{ unit.label }}
        </span>
      </div>
    </div>

    <div
      class="bg-background-positive-muted flex items-start gap-2 rounded-[20px] px-3 py-2"
    >
      <ClockIcon class="text-background-positive-primary mt-px shrink-0" />
      <p class="text-text-neutral-secondary text-xs">
        {{
          isPromoApplicable
            ? t('cashbackInfo.countdown.noteActive', {
                date: dateUtils.format(effectiveEndDate, 'D MMMM'),
              })
            : t('cashbackInfo.countdown.noteInactive')
        }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import dayjs from 'dayjs';
  import { dateUtils } from 'shared';

  import ClockIcon from '../assets/20/clock.svg?component';
  import messages from '../locales.json';

  const props = defineProps<{
    promoEndDate: string | null;
    isPromoApplicable: boolean;
  }>();

  const emit = defineEmits<{
    onExpired: [];
  }>();

  const { t } = useI18n({ messages });

  const unitKeys: UnitKey[] = ['days', 'hours', 'minutes', 'seconds'];
  const fallbackEndDate = dayjs().add(30, 'day').toISOString();

  const now = ref(Date.now());
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const effectiveEndDate = computed(
    () => props.promoEndDate ?? fallbackEndDate,
  );

  const timeLeft = computed(() => {
    const end = new Date(effectiveEndDate.value).getTime();
    const diff = Math.max(0, end - now.value);
    return {
      days: Math.floor(diff / 1000 / 60 / 60 / 24),
      hours: Math.floor((diff / 1000 / 60 / 60) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  });

  const countdownUnits = computed(() => {
    const labels: Record<UnitKey, string> = {
      days: t('cashbackInfo.countdown.days'),
      hours: t('cashbackInfo.countdown.hours'),
      minutes: t('cashbackInfo.countdown.minutes'),
      seconds: t('cashbackInfo.countdown.seconds'),
    };
    return unitKeys.map(key => ({
      key,
      label: labels[key],
      state: flipStates.value[key],
    }));
  });

  const pad = (n: number) => String(n).padStart(2, '0');
  const getFontClass = (value: string) =>
    value.length >= 3 ? 'text-[30px]' : 'text-[40px]';

  type UnitKey = 'days' | 'hours' | 'minutes' | 'seconds';

  interface FlipState {
    current: string;
    previous: string;
    currentFontClass: string;
    previousFontClass: string;
    flipping: boolean;
  }

  const makeState = (value: string): FlipState => ({
    current: value,
    previous: value,
    currentFontClass: getFontClass(value),
    previousFontClass: getFontClass(value),
    flipping: false,
  });

  // Initialize immediately so digits appear on first render without waiting for
  // the first interval tick.
  const tl = timeLeft.value;
  const flipStates = ref<Record<UnitKey, FlipState>>({
    days: makeState(pad(tl.days)),
    hours: makeState(pad(tl.hours)),
    minutes: makeState(pad(tl.minutes)),
    seconds: makeState(pad(tl.seconds)),
  });

  // When timeLeft changes (once per second), drive the flip animation for each
  // unit whose value has changed.
  watch(timeLeft, newVal => {
    if (!newVal.days && !newVal.hours && !newVal.minutes && !newVal.seconds) {
      emit('onExpired');
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      return;
    }

    for (const key of unitKeys) {
      const newStr = pad(newVal[key]);
      const state = flipStates.value[key];
      if (newStr === state.current) {
        continue;
      }

      if (!props.isPromoApplicable) {
        state.current = newStr;
        state.currentFontClass = getFontClass(newStr);
        continue;
      }

      state.previousFontClass = state.currentFontClass;
      state.previous = state.current;
      state.current = newStr;
      state.currentFontClass = getFontClass(newStr);
      state.flipping = true;

      // Keep flipping true for a bit longer than the CSS animation (500 ms)
      // so the card isn't torn away while still mid-frame.
      setTimeout(() => {
        flipStates.value[key].flipping = false;
      }, 600);
    }
  });

  onMounted(() => {
    if (props.isPromoApplicable) {
      intervalId = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    }
  });

  onUnmounted(() => {
    if (intervalId !== null) {
      clearInterval(intervalId);
    }
  });
</script>

<style scoped>
  /*
  Phase 1 — old top half folds downward, rotating around its bottom edge.
  As it approaches −90° it becomes edge-on (invisible) and the static top
  (already showing the new digit) is revealed beneath it.
*/
  .flip-card-top {
    transform-origin: center bottom;
    animation: flip-card-top 0.25s ease-in forwards;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  /*
  Phase 2 — new bottom half unfolds from flat (90°) to fully open (0°),
  delayed so it starts exactly when phase 1 ends.
*/
  .flip-card-bottom {
    transform-origin: center top;
    animation: flip-card-bottom 0.25s ease-out 0.25s both;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  @keyframes flip-card-top {
    from {
      transform: rotateX(0deg);
    }
    to {
      transform: rotateX(-90deg);
    }
  }

  @keyframes flip-card-bottom {
    from {
      transform: rotateX(90deg);
    }
    to {
      transform: rotateX(0deg);
    }
  }
</style>
